import { useEffect, useState } from 'react';
import { Select } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import type { Location, Organization, Practitioner, Resource } from '@medplum/fhirtypes';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';

import type { ClaimReferenceResourceType } from '../types';

export function displayResource(resource: Resource): string {
  if (resource.resourceType === 'Organization') {
    return (resource as Organization).name ?? resource.id ?? 'Organization';
  }
  if (resource.resourceType === 'Location') {
    return (resource as Location).name ?? resource.id ?? 'Location';
  }
  if (resource.resourceType === 'Practitioner') {
    return formatPractitionerStaffLabel(resource as Practitioner) || resource.id || 'Practitioner';
  }
  return resource.id ?? resource.resourceType;
}

export function ClaimFhirReferenceSelect({
  resourceType,
  value,
  onChange,
  persistFullReference,
  searchType,
}: {
  resourceType: ClaimReferenceResourceType;
  value: string;
  onChange: (value: string) => void;
  /** When false, persist resource id only (condition payorId). */
  persistFullReference: boolean;
  /** Organization `type` search filter, e.g. ORG_TYPE_PAYER for payer-only lists. */
  searchType?: string;
}): JSX.Element {
  const medplum = useMedplum();
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    medplum
      .searchResources(resourceType, {
        ...(searchType ? { type: searchType } : {}),
        _count: '200',
        _sort: 'name',
      })
      .then((bundle) => {
        if (cancelled) return;
        const data = bundle.map((r) => {
          const id = r.id ?? '';
          const ref = persistFullReference ? `${resourceType}/${id}` : id;
          return { value: ref, label: displayResource(r) };
        }).filter((o) => o.value);
        if (value && !data.some((o) => o.value === value)) {
          data.unshift({ value, label: value });
        }
        setOptions(data);
      })
      .catch(() => {
        if (!cancelled) setOptions(value ? [{ value, label: value }] : []);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, persistFullReference, resourceType, searchType, value]);

  return (
    <Select
      label={resourceType}
      searchable
      clearable
      data={options}
      value={value || null}
      onChange={(v) => onChange(v ?? '')}
    />
  );
}
